import { notification } from 'antd';
import { generatePath } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import { MultipleOriginsProductRawMaterial } from 'models/MultipleOriginsProductRawMaterial/multipleOriginsProductRawMaterial.model';
import React from 'react';
import { FormikProps } from 'formik';
import { Address } from 'models/Address/address.model';
import { Origin, rawMaterialPayload } from '../CreateProductFlowInterfaces';
import { FieldProps, RawMaterialProps } from 'enums/RawMaterialEnums';
import { scrollIntoView } from 'shared/utils/scrollIntoView';

export const handleFormSubmission = async (
  payload: rawMaterialPayload,
  isDisabled: boolean,
  navigate: (path: string) => void,
  id: string | undefined,
  productId: number,
  rawMaterialList: MultipleOriginsProductRawMaterial[],
  updateRawMaterial: Function,
  createProductRawMaterial: Function,
  transformPayload: Function
) => {
  if (isDisabled) {
    navigate(
      generatePath(
        `${AppRoutes.CREATE_PRODUCT}/${AppRoutes.CREATE_PRODUCT_RAW_MATERIAL_TRANSPORTATION}`,
        { id: id }
      )
    );
  } else {
    const transformedPayload = transformPayload(payload);
    let message;
    if (rawMaterialList.length > 0) {
      message = await updateRawMaterial(
        productId,
        transformedPayload as MultipleOriginsProductRawMaterial
      );
    } else {
      message = await createProductRawMaterial(
        productId,
        transformedPayload as MultipleOriginsProductRawMaterial
      );
    }
    if (message && message.length > 0) {
      notification.warning({
        message: message.map((item: string, index: number) => (
          <p key={index}>
            {index + 1}. {item}
          </p>
        )),
        duration: 8,
      });
    }
  }
};

export const handleSaveAsDraft = async (
  payload: rawMaterialPayload,
  productId: number,
  rawMaterialList: MultipleOriginsProductRawMaterial[],
  saveAsDraftUpdateRawMaterial: Function,
  saveAsDraftCreateRawMaterial: Function,
  transformPayload: Function
) => {
  const transformedPayload = transformPayload(payload);
  let message: string[];
  if (rawMaterialList.length > 0) {
    message = await saveAsDraftUpdateRawMaterial(
      productId,
      transformedPayload as MultipleOriginsProductRawMaterial,
      {
        save_draft: true,
      }
    );
  } else {
    message = await saveAsDraftCreateRawMaterial(
      productId,
      transformedPayload as MultipleOriginsProductRawMaterial,
      {
        save_draft: true,
      }
    );
  }
  if (message && message.length > 0) {
    notification.success({
      message: message.map((item: string, index: number) => (
        <p key={index}>
          {index + 1}. {item}
        </p>
      )),
      duration: 8,
    });
  }
};

export const showLocationModal = (
  index: number,
  type: string,
  setSelectedType: (type: string | undefined) => void,
  setSelectedIndex: (index: number | undefined) => void,
  setSelectedIndexAddress: (address: Origin[]) => void,
  setSelectedRecordIndex: (index: number | undefined) => void,
  setSelectedRecordType: (type: string | undefined) => void,
  setSelectedOriginIndex: (index: number | undefined) => void,
  selectedAddress?: Origin[],
  selectedRecordIndex?: number,
  selectedRecordType?: string,
  selectedOriginIndex?: number
) => {
  setSelectedType(type);
  setSelectedIndex(index);
  selectedAddress && setSelectedIndexAddress(selectedAddress);
  setSelectedRecordIndex(selectedRecordIndex);
  setSelectedRecordType(selectedRecordType);
  setSelectedOriginIndex(selectedOriginIndex);
};

export const closeLocationModal = (
  confirmation: boolean | undefined,
  setIsConfirmationModalVisible: (visible: boolean) => void,
  setSelectedType: (type: string | undefined) => void,
  setSelectedIndex: (index: number | undefined) => void
) => {
  if (confirmation) setIsConfirmationModalVisible(true);
  else {
    setSelectedType(undefined);
    setSelectedIndex(undefined);
  }
};

export const handleAddOrigins = (
  formikProps: FormikProps<rawMaterialPayload>,
  index: number,
  type: string | undefined,
  id: string | undefined,
  isDisabled: boolean
) => {
  if (type === RawMaterialProps.INGREDIENT) {
    if (!isDisabled) {
      const newOrigins = [
        ...(formikProps.values.ingredients[index].origins || []),
        {
          id: undefined,
          ingredientOrigin: undefined,
          bestfitRegion: undefined,
          originShare: undefined,
          percentage: '%',
          address: new Address(),
        },
      ];
      if (formikProps?.values?.ingredients[index]?.origins?.length === 0) {
        newOrigins[0].id = formikProps.values.ingredients[index].id;
        newOrigins[0].ingredientOrigin =
          formikProps.values.ingredients[index].ingredientOrigin;
        newOrigins[0].bestfitRegion =
          formikProps.values.ingredients[index].bestFitProductionRegion;
        newOrigins[0].address = formikProps.values.ingredients[index].address;
      }
      formikProps.setFieldValue(
        `${RawMaterialProps.INGREDIENTS}[${index}].${RawMaterialProps.ORIGINS}`,
        newOrigins
      );
      formikProps.setFieldValue(
        `${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_ORIGIN}`,
        undefined
      );
      formikProps.setFieldValue(
        `${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.INGREDIENT_REGION}`,
        undefined
      );
      formikProps.setFieldValue(
        `${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.ADDRESS}`,
        new Address()
      );
      formikProps.setFieldValue(
        `${RawMaterialProps.INGREDIENTS}[${index}].${FieldProps.ID}`,
        undefined
      );
    }
  } else {
    if (!isDisabled) {
      const newOrigins = [
        ...(formikProps.values.packageMaterials[index].origins || []),
        {
          id: undefined,
          ingredientOrigin: undefined,
          bestfitRegion: undefined,
          originShare: undefined,
          percentage: '%',
          address: new Address(),
        },
      ];
      if (formikProps.values.packageMaterials[index].origins?.length === 0) {
        newOrigins[0].id = formikProps.values.packageMaterials[index].id;
        newOrigins[0].ingredientOrigin =
          formikProps.values.packageMaterials[index].packingMaterialOrigin;
        newOrigins[0].bestfitRegion =
          formikProps.values.packageMaterials[
            index
          ].packingMaterialbestFitProductionRegion;
        newOrigins[0].address =
          formikProps.values.packageMaterials[index].address;
      }
      formikProps.setFieldValue(
        `${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${RawMaterialProps.ORIGINS}`,
        newOrigins
      );
      formikProps.setFieldValue(
        `${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_ORIGIN}`,
        undefined
      );
      formikProps.setFieldValue(
        `${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.PACKAGING_REGION}`,
        undefined
      );
      formikProps.setFieldValue(
        `${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.ADDRESS}`,
        new Address()
      );
      formikProps.setFieldValue(
        `${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${FieldProps.ID}`,
        undefined
      );
    }
  }
  scrollIntoView(id);
};

export const handleRemoveOrigins = (
  formikProps: FormikProps<rawMaterialPayload>,
  index: number,
  originIndex: number,
  type: string | undefined
) => {
  if (type === RawMaterialProps.INGREDIENT) {
    const newOrigins = [
      ...(formikProps.values.ingredients[index].origins || []),
    ];
    newOrigins.splice(originIndex, 1); // Remove the origin object
    formikProps.setFieldValue(
      `${RawMaterialProps.INGREDIENTS}[${index}].${RawMaterialProps.ORIGINS}`,
      newOrigins
    );
  } else {
    const newOrigins = [
      ...(formikProps.values.packageMaterials[index].origins || []),
    ];
    newOrigins.splice(originIndex, 1); // Remove the origin object
    formikProps.setFieldValue(
      `${RawMaterialProps.PACKAGE_MATERIAL}[${index}].${RawMaterialProps.ORIGINS}`,
      newOrigins
    );
  }
};

export const handleSaveLocationData = (
  data: Origin[],
  formikProps: FormikProps<rawMaterialPayload>,
  selectedRecordType: string | undefined,
  selectedRecordIndex: number | undefined,
  handleCloseLocationModal: (confirmation?: boolean) => void
) => {
  if (data.length === 1) {
    formikProps.setFieldValue(
      `${selectedRecordType}[${selectedRecordIndex}].${RawMaterialProps.ADDRESS}`,
      data[0].address
    );
    formikProps.setFieldValue(
      `${selectedRecordType}[${selectedRecordIndex}].${RawMaterialProps.ORIGINS}`,
      []
    );
    if (selectedRecordType === RawMaterialProps.INGREDIENTS) {
      formikProps.setFieldValue(
        `${selectedRecordType}[${selectedRecordIndex}].${RawMaterialProps.INGREDIENT_ORIGIN}`,
        data[0].ingredientOrigin
      );
      formikProps.setFieldValue(
        `${selectedRecordType}[${selectedRecordIndex}].${FieldProps.INGREDIENT_REGION}`,
        data[0].bestfitRegion
      );
    } else {
      formikProps.setFieldValue(
        `${selectedRecordType}[${selectedRecordIndex}].${FieldProps.PACKAGING_ORIGIN}`,
        data[0].ingredientOrigin
      );
      formikProps.setFieldValue(
        `${selectedRecordType}[${selectedRecordIndex}].${FieldProps.PACKAGING_REGION}`,
        data[0].bestfitRegion
      );
    }
  } else {
    formikProps.setFieldValue(
      `${selectedRecordType}[${selectedRecordIndex}].${RawMaterialProps.ORIGINS}`,
      data
    );
    formikProps.setFieldValue(
      `${selectedRecordType}[${selectedRecordIndex}].${RawMaterialProps.ADDRESS}`,
      new Address()
    );
    if (selectedRecordType === RawMaterialProps.INGREDIENTS) {
      formikProps.setFieldValue(
        `${selectedRecordType}[${selectedRecordIndex}].${RawMaterialProps.INGREDIENT_ORIGIN}`,
        undefined
      );
      formikProps.setFieldValue(
        `${selectedRecordType}[${selectedRecordIndex}].${FieldProps.INGREDIENT_REGION}`,
        undefined
      );
    } else {
      formikProps.setFieldValue(
        `${selectedRecordType}[${selectedRecordIndex}].${FieldProps.PACKAGING_ORIGIN}`,
        undefined
      );
      formikProps.setFieldValue(
        `${selectedRecordType}[${selectedRecordIndex}].${FieldProps.PACKAGING_REGION}`,
        undefined
      );
    }
  }
  handleCloseLocationModal(false);
};

export const getSelectedIndexAddress = (
  formikProps: FormikProps<rawMaterialPayload>,
  selectedType: string | undefined,
  selectedIndex: number | undefined
) => {
  if (selectedType === RawMaterialProps.INGREDIENTS) {
    return formikProps.values.ingredients[selectedIndex ?? -1]?.address;
  } else {
    return formikProps.values.packageMaterials[selectedIndex ?? -1]?.address;
  }
};
